import type { ChangeEventHandler, FC } from "react";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button, RadioButton, RadioButtonGroup } from "@jti/core-components";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";

import type { QuizAnswerState } from "../types";
import { QUIZ_ANSWER_STATE, replaceProgressPlaceholders } from "../types";

import styles from "./QuizQuestion.module.scss";

export type QuizQuestionOption = {
  value: string;
  label: string;
};

export type QuizQuestionAnswerState = QuizAnswerState;

export type QuizQuestionProps = {
  questionNumber: number;
  totalQuestions: number;
  title: string;
  description?: string;
  options: QuizQuestionOption[];
  selectedValue: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  answerState?: QuizQuestionAnswerState;
  correctFeedbackText?: string;
  incorrectFeedbackText?: string;
  onNext: () => void;
  nextButtonLabel?: string;
  progressTemplate: string;
  isSubmitting?: boolean;
};

const getRadioTabIndex = (optionValue: string, selectedValue: string, isFirst: boolean): number => {
  if (!selectedValue) return isFirst ? 0 : -1;

  return selectedValue === optionValue ? 0 : -1;
};

export const QuizQuestion: FC<QuizQuestionProps> = ({
  questionNumber,
  totalQuestions,
  title,
  description,
  options,
  selectedValue,
  onChange,
  answerState = QUIZ_ANSWER_STATE.idle,
  correctFeedbackText,
  incorrectFeedbackText,
  onNext,
  nextButtonLabel,
  progressTemplate,
  isSubmitting = false,
}) => {
  const isNextEnabled = answerState === QUIZ_ANSWER_STATE.correct;
  const progressText = replaceProgressPlaceholders(progressTemplate, questionNumber, totalQuestions);

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollableContent}>
        <div className={styles.header}>
          <p id={`quiz-progress-${questionNumber}`} className={styles.progress}>
            {progressText}
          </p>
          <h2 id={`quiz-question-title-${questionNumber}`} className={styles.title}>
            {title}
          </h2>
          {description && (
            <p id={`quiz-question-desc-${questionNumber}`} className={styles.description}>
              {description}
            </p>
          )}
        </div>
        <div className={styles.questionsWrapper}>
          <div className={styles.answersContainer}>
            <RadioButtonGroup
              name={`quiz-question-${questionNumber}`}
              aria-labelledby={`quiz-question-title-${questionNumber}`}
              aria-describedby={description ? `quiz-question-desc-${questionNumber}` : undefined}
            >
              {options.map((option, index) => (
                <RadioButton
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  checked={selectedValue === option.value}
                  tabIndex={getRadioTabIndex(option.value, selectedValue, index === 0)}
                  onChange={onChange}
                />
              ))}
            </RadioButtonGroup>
          </div>
          {answerState === QUIZ_ANSWER_STATE.correct && correctFeedbackText && (
            <AlertBanner tone="success" description={correctFeedbackText} role="status" />
          )}
          {answerState === QUIZ_ANSWER_STATE.incorrect && incorrectFeedbackText && (
            <AlertBanner key={selectedValue} tone="error" description={incorrectFeedbackText} role="alert" />
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          variant="text-muted"
          iconAfter={LongArrowForwardIcon}
          onClick={onNext}
          disabled={!isNextEnabled || isSubmitting}
          aria-describedby={`quiz-progress-${questionNumber}`}
        >
          {nextButtonLabel}
        </Button>
      </div>
    </div>
  );
};

QuizQuestion.displayName = "QuizQuestion";
